import { inject, Injectable } from '@angular/core';
import { CheckType } from '@app/core/model/entities/asset/check-type';
import ApiService from '@services/api.service';
import { AppManager } from '@services/managers/app.manager';
import { gql } from 'apollo-angular';
import { plainToInstance } from 'class-transformer';
import { Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class CheckTypeService {
  private readonly apiService = inject(ApiService);
  private readonly appManager = inject(AppManager);

  private checkTypesSubject = new ReplaySubject<{ [k: string]: CheckType }>(1);
  public hasCheckTypes: boolean = false;

  private readonly checkInfoGraphqlFragment = gql`
    fragment CheckTypeInfo on CheckType {
      code
      resultFieldsList {
        fieldCode
        valuesList {
          value
          severity
        }
      }
    }
  `;

  /**
   * Replay the checkTypes previously fetched with {@link loadChecksTypeData}. The returning Observable will never
   * complete if {@link loadChecksTypeData} is never called.
   * @return An Observable emitting a map of check types, with check types codes as keys and check types data as values.
   */
  public get checkTypes$(): Observable<{ [k: string]: CheckType }> {
    return this.checkTypesSubject.asObservable();
  }


  /**
   * Call the API to fetch all check types for the current organization.
   * @return A map with check types' codes as keys and the checkTypes data as values.
   */
  public loadChecksTypeData(): Observable<{ [k: string]: CheckType }> {
    const query = gql`
      query CheckTypes($organizationId: String!) {
        checkTypes(organizationId: $organizationId) {
          ...CheckTypeInfo
        }
      }${this.checkInfoGraphqlFragment}
    `;
    const variables = {
      organizationId: this.appManager.currentOrganization.id
    };

    return this.apiService.query({query, variables})
      .pipe(
        map(data => {
          const checkTypesList = plainToInstance(CheckType, data['checkTypes'] as CheckType[]);
          const checkTypes: { [k: string]: CheckType } = {};
          checkTypesList.forEach((type) => checkTypes[type.code] = type);
          this.hasCheckTypes = checkTypesList.length > 0;
          this.checkTypesSubject.next(checkTypes);
          return checkTypes;
        })
      );
  }

  /**
   * Get a check type by its code.
   * @param code Check type's code.
   * @return Observable emitting a single CheckType entity.
   */
  public getCheckType(code: string): Observable<CheckType> {
    const query = gql`
      query CheckType($code: String!, $organizationId: String!) {
        checkType(code: $code, organizationId: $organizationId) {
          ...CheckTypeInfo
        }
      }${this.checkInfoGraphqlFragment}
    `;
    const variables = {
      code,
      organizationId: this.appManager.currentOrganization.id
    };

    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(CheckType, data['checkType'])));
  }
}
